import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import type { BannerOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import type { PreviewModel } from '@/components/user_catalog/banners/bannerPreview';
import { BANNER_SHOW_TABS } from '../constants';
import type { BannerShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<BannerShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface BannerShowHeaderProps {
  record: BannerOut;
  model: PreviewModel;
  activeTab: BannerShowTab;
  onTabChange: (key: string) => void;
}

export const BannerShowHeader = ({
  record,
  model,
  activeTab,
  onTabChange,
}: BannerShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell
              label={t('user_catalog.banners.fields.banner_actions_count')}
            >
              {record.banner_links.length}
            </StatCell>
            <StatCell label={t('user_catalog.banner_actions.fields.size')}>
              {orDash(
                model.effectiveSize &&
                  t(`user_catalog.banner_actions.size.${model.effectiveSize}`)
              )}
            </StatCell>
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={BANNER_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
